/**
 * The agent's `connect_apps` card: the apps it needs, each with its logo, its
 * connection state and a Connect button that opens the provider's sign-in in
 * another tab. The turn is paused on this card (a client-resolved interrupt,
 * like `ask_user`): once every app reports connected the card writes the
 * outcome as the tool part's output and the turn resumes on its own; "Skip for
 * now" resumes it without connecting. The links live only on this card, never
 * in what the model reads.
 */
import type { DynamicToolUIPart, ToolUIPart } from "ai";
import { CheckCircle2, ExternalLink, Loader2, Plug } from "lucide-react";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC
} from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import {
  useConnectionWatch,
  type WatchedStatus
} from "../hooks/useConnectionWatch";
import type { ConnectApp, ConnectAppsInput, ConnectAppsOutput } from "../types";
import { ConnectorLogo } from "./ConnectorLogo";
import { ConnectorStatusBadge } from "./ConnectorStatusBadge";

type AnyToolPart = ToolUIPart | DynamicToolUIPart;

type Props = {
  part: AnyToolPart;
  /** Writes the card's outcome as the part's output (resumes the turn). */
  onResolve?: (toolCallId: string, output: ConnectAppsOutput) => void;
  className?: string;
};

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null;

const str = (v: unknown): string | null =>
  typeof v === "string" && v.trim() ? v : null;

/** The apps on the card, from the part's input as the server announced it. */
export const connectAppsFromInput = (input: unknown): ConnectAppsInput => {
  if (!isRecord(input)) return { toolkits: [], apps: [] };
  const toolkits = Array.isArray(input.toolkits)
    ? input.toolkits.filter((t): t is string => typeof t === "string")
    : [];
  const apps: ConnectApp[] = Array.isArray(input.apps)
    ? input.apps.flatMap((a): ConnectApp[] => {
        if (!isRecord(a) || !str(a.slug)) return [];
        return [
          {
            slug: a.slug as string,
            name: str(a.name) ?? (a.slug as string),
            logo: str(a.logo),
            status: (str(a.status) as ConnectApp["status"]) ?? "unknown",
            redirect_url: str(a.redirect_url),
            error: str(a.error) ?? undefined
          }
        ];
      })
    : [];
  return { toolkits, apps };
};

const outputOf = (part: AnyToolPart): ConnectAppsOutput | null => {
  if (part.state !== "output-available" || !isRecord(part.output)) return null;
  const apps = Array.isArray(part.output.apps)
    ? part.output.apps.flatMap((a) =>
        isRecord(a) && str(a.slug)
          ? [
              {
                slug: a.slug as string,
                name: str(a.name) ?? undefined,
                status: str(a.status) ?? "unknown"
              }
            ]
          : []
      )
    : undefined;
  return { apps, skipped: Boolean(part.output.skipped) };
};

const listNames = (names: string[]): string =>
  names.length <= 1
    ? names[0] ?? "an app"
    : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;

const openLink = (url: string, name: string) => {
  const opened = window.open(url, "_blank", "noopener,noreferrer");
  if (!opened) {
    toast.info(`Open the ${name} sign-in to continue`, {
      duration: 60_000,
      action: {
        label: "Open",
        onClick: () => window.open(url, "_blank", "noopener,noreferrer")
      }
    });
  }
};

export const ConnectAppsCard: FC<Props> = ({ part, onResolve, className }) => {
  const { apps } = useMemo(
    () => connectAppsFromInput(part.input),
    [part.input]
  );
  const output = outputOf(part);
  // Apps the provider knows and that still need the attorney.
  const required = useMemo(
    () => apps.filter((a) => a.status !== "unknown"),
    [apps]
  );
  const initial = useMemo(
    () =>
      Object.fromEntries(apps.map((a) => [a.slug, a.status])) as Record<
        string,
        WatchedStatus
      >,
    [apps]
  );
  const resolved = useRef(false);
  const [resolving, setResolving] = useState(false);

  const resolve = useCallback(
    (out: ConnectAppsOutput) => {
      if (resolved.current || !onResolve) return;
      resolved.current = true;
      setResolving(true);
      onResolve(part.toolCallId, out);
    },
    [onResolve, part.toolCallId]
  );

  const watch = useConnectionWatch(initial, {
    onAllConnected: (statuses) =>
      resolve({
        apps: required.map((a) => ({
          slug: a.slug,
          name: a.name,
          status: statuses[a.slug] ?? "connected"
        }))
      })
  });
  const { statuses, watching, timedOut } = watch;

  // Every required app already connected (a reload after the sign-in): resume.
  const allConnected =
    required.length > 0 &&
    required.every((a) => statuses[a.slug] === "connected");
  useEffect(() => {
    if (allConnected && !output) {
      resolve({
        apps: required.map((a) => ({
          slug: a.slug,
          name: a.name,
          status: "connected"
        }))
      });
    }
  }, [allConnected, output, required, resolve]);

  if (!apps.length) return null;

  if (output) {
    const byStatus = new Map(
      (output.apps ?? []).map((a) => [a.slug, a.status])
    );
    const connected = apps.filter(
      (a) => (byStatus.get(a.slug) ?? a.status) === "connected"
    );
    const summary = output.skipped
      ? `Skipped connecting ${listNames(apps.map((a) => a.name))}`
      : connected.length
      ? `${listNames(connected.map((a) => a.name))} connected`
      : `${listNames(apps.map((a) => a.name))} not connected`;
    return (
      <div
        className={cn(
          "flex items-center gap-2 rounded-md bg-fill-1 px-3 py-2 text-base",
          className
        )}
        data-testid="connect-apps-card-resolved"
        data-skipped={output.skipped ? "" : undefined}
      >
        {output.skipped || !connected.length ? (
          <Plug className="size-3.5 shrink-0 text-muted-foreground" />
        ) : (
          <CheckCircle2 className="size-3.5 shrink-0 text-success" />
        )}
        <div className="flex -space-x-1.5">
          {apps.slice(0, 4).map((a) => (
            <ConnectorLogo
              key={a.slug}
              name={a.name}
              src={a.logo}
              className="size-6 rounded-md [&>img]:size-4"
            />
          ))}
        </div>
        <span className="min-w-0 flex-1 truncate font-medium">{summary}</span>
      </div>
    );
  }

  const busy = part.state === "input-streaming";
  const title = `Connect ${listNames(required.map((a) => a.name))}`;

  return (
    <div
      className={cn(
        "rounded-xl bg-elevated p-3 text-base inset-ring inset-ring-line-2",
        (busy || resolving) && "opacity-70",
        className
      )}
      data-testid="connect-apps-card"
      data-resolving={resolving ? "" : undefined}
    >
      <div className="flex items-center gap-1.5 text-muted-foreground">
        <Plug className="size-3.5 shrink-0" />
        <span className="truncate">{title}</span>
      </div>
      <p className="mt-1 text-sm text-muted-foreground">
        The agent needs access to these apps to continue. Sign in with each
        provider in the tab that opens; this card updates when you are done.
      </p>
      <ul className="mt-2.5 divide-y divide-line-1 rounded-md bg-fill-1">
        {apps.map((app) => {
          const status = statuses[app.slug] ?? app.status;
          const connecting = watching.includes(app.slug);
          const isConnected = status === "connected";
          const canConnect =
            !isConnected && status !== "unknown" && Boolean(app.redirect_url);
          return (
            <li
              key={app.slug}
              className="flex items-center gap-3 px-2.5 py-2"
              data-testid={`connect-app-${app.slug}`}
              data-status={connecting ? "connecting" : status}
            >
              <ConnectorLogo
                name={app.name}
                src={app.logo}
                className="size-8 [&>img]:size-5"
              />
              <div className="min-w-0 flex-1">
                <div className="truncate font-medium">{app.name}</div>
                {app.error ? (
                  <div className="truncate text-xs text-destructive">
                    {app.error}
                  </div>
                ) : status === "unknown" ? (
                  <div className="text-xs text-muted-foreground">
                    This app is not available to connect.
                  </div>
                ) : null}
              </div>
              <ConnectorStatusBadge status={status} connecting={connecting} />
              {canConnect ? (
                <Button
                  size="xs"
                  variant={status === "error" ? "outline" : "default"}
                  className="h-7 gap-1 px-2.5 text-sm"
                  disabled={busy || resolving}
                  onClick={() => {
                    openLink(app.redirect_url as string, app.name);
                    watch.watch(app.slug);
                  }}
                  data-testid={`connect-app-open-${app.slug}`}
                >
                  {connecting ? (
                    <Loader2 className="size-3 animate-spin" />
                  ) : (
                    <ExternalLink className="size-3" />
                  )}
                  {status === "error"
                    ? "Reconnect"
                    : connecting
                    ? "Reopen"
                    : "Connect"}
                </Button>
              ) : null}
            </li>
          );
        })}
      </ul>
      <div className="mt-2 flex items-center justify-between gap-2">
        <span className="text-sm text-muted-foreground">
          {resolving
            ? "Connected. Continuing…"
            : timedOut
            ? "Still waiting. Finish the sign-in in the other tab, or skip for now."
            : watching.length
            ? "Waiting for you to finish signing in…"
            : ""}
        </span>
        {onResolve && !resolving ? (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-sm text-muted-foreground"
            disabled={busy}
            onClick={() => {
              watch.stop();
              resolve({ skipped: true });
            }}
            data-testid="connect-apps-skip"
          >
            Skip for now
          </Button>
        ) : null}
      </div>
    </div>
  );
};
